/**
 * MpMenus.tsx —— 多人牌桌顶栏的「手牌历史」与「设置」入口（D-S4）。
 *
 * 胜率门控红线（AC3.1）：
 *   - 设置面板中的「赢率计算器」开关在多人模式下**恒为置灰不可用**，且不展示任何百分比数值；
 *   - 手牌历史只列出「手数 / 盈亏」等中性信息；只有在**本手已结束**时才允许打开复盘（赢率解禁），
 *     对局进行中「查看复盘」按钮置灰。
 */

import { useEffect } from 'react';
import { mpClient, type MpState } from '../../net/mpClient';
import { chips } from '../../state/format';
import { setLetwayFloat } from '../../state/letway';
import { patchSettings } from '../../state/settings';
import { useSettings } from '../hooks';
import { Switch } from '../primitives';

const EQUITY_LOCK_HINT = '多人对局中实时赢率已锁定，本手结束后可在复盘查看。';

/** 二期 FR-E2：抽屉（历史/设置）在场登记为 mp-drawer 查阅面板 → 触发 pwa 让位（不静默 toast，AC-E2 矩阵 review/drawer 行）。 */
function useMpDrawerLetway(): void {
  useEffect(() => {
    setLetwayFloat('mp-drawer', true);
    return () => setLetwayFloat('mp-drawer', false);
  }, []);
}

export function MpHistoryDrawer({ state, onClose }: { state: MpState; onClose: () => void }) {
  useMpDrawerLetway();
  const handOver = state.view?.phase === 'handComplete' || state.view?.phase === 'gameOver';
  const items = [...state.history].sort((a, b) => b.handNumber - a.handNumber);

  return (
    <div className="mp-drawer" role="dialog" aria-label="手牌历史">
      <div className="mp-drawer__scrim" onClick={onClose} />
      <div className="mp-drawer__panel">
        <header className="mp-drawer__head">
          <h2>手牌历史</h2>
          <button className="mp-btn mp-btn--ghost mp-btn--tiny" onClick={onClose}>关闭 ✕</button>
        </header>
        <p className="mp-drawer__lock">{EQUITY_LOCK_HINT}</p>
        {items.length === 0 ? (
          <p className="mp-hint">本场还没有已结束的手牌。</p>
        ) : (
          <ul className="mp-hist">
            {items.map((h) => {
              const net = h.net.find((x) => x.seat === h.yourSeat)?.net ?? 0;
              return (
                <li key={h.handNumber} className="mp-hist__row">
                  <span className="mp-hist__no">第 {h.handNumber} 手</span>
                  <span className={`mp-hist__net ${net >= 0 ? 'is-up' : 'is-down'}`}>
                    {net >= 0 ? '+' : ''}{chips(net)}
                  </span>
                  <button
                    className="mp-btn mp-btn--tiny"
                    disabled={!handOver}
                    title={handOver ? '' : EQUITY_LOCK_HINT}
                    onClick={() => { mpClient.openHistoryReview(h.handNumber); onClose(); }}
                  >
                    查看复盘
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export function MpSettingsPanel({ onClose }: { onClose: () => void }) {
  useMpDrawerLetway();
  const s = useSettings();
  return (
    <div className="mp-drawer" role="dialog" aria-label="设置">
      <div className="mp-drawer__scrim" onClick={onClose} />
      <div className="mp-drawer__panel">
        <header className="mp-drawer__head">
          <h2>设置</h2>
          <button className="mp-btn mp-btn--ghost mp-btn--tiny" onClick={onClose}>关闭 ✕</button>
        </header>

        <div className="mp-set-row">
          <span className="mp-set-row__label">四色牌面</span>
          <Switch checked={s.fourColor} onChange={(v) => patchSettings({ fourColor: v })} label="四色牌面" />
        </div>

        <div className="mp-set-row">
          <span className="mp-set-row__label">动效</span>
          <Switch
            checked={s.motion !== 'off'}
            onChange={(v) => patchSettings({ motion: v ? 'on' : 'off' })}
            label="动效"
          />
        </div>

        {/* 门控可见性表达：开关存在但恒置灰，且不展示任何赢率数值 */}
        <div className="mp-set-row mp-set-row--locked">
          <span className="mp-set-row__label">
            🔒 赢率计算器
            <span className="mp-set-row__hint">{EQUITY_LOCK_HINT}</span>
          </span>
          <Switch checked={false} disabled onChange={() => { /* 多人模式恒禁用 */ }} label="赢率计算器（多人对局中锁定）" />
        </div>
      </div>
    </div>
  );
}
